import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  AcademicCapIcon, 
  UserGroupIcon, 
  BookOpenIcon, 
  ClockIcon, 
  ClipboardDocumentListIcon,
  ClipboardDocumentCheckIcon,
  ChartBarIcon,
  PlusIcon,
  ArrowRightOnRectangleIcon,
  HomeIcon,
  BellIcon,
  UserCircleIcon,
  ChevronDownIcon,
  PencilSquareIcon
} from '@heroicons/react/24/outline';
import { usePermissions } from '../hooks/usePermissions';
import ProtectedNavLink from '../components/ProtectedNavLink';
import AccessDenied from '../components/AccessDenied';
import { useAuth } from '../contexts/AuthContext';
import CourseSelector from '../components/CourseSelector';
import { useQuery } from '@tanstack/react-query';
import axios from '../utils/axiosConfig';

import AlumnosPage from './admin/AlumnosPage';
import ProfesoresPage from './admin/ProfesoresPage';
import CursosPage from './admin/CursosPage';
import TurnosPage from './admin/TurnosPage';
import InscripcionesPage from './admin/InscripcionesPage';
import NotasPage from './admin/NotasPage';
import AyudanteDashboard from './admin/AyudanteDashboard';
import ProfesorDashboard from './admin/ProfesorDashboard';
import JTPDashboard from './admin/JTPDashboard';
import RectificarNotasPage from './admin/RectificarNotasPage';
import ListadoNotasPage from './admin/ListadoNotasPage';
import ReporteGuaraniPage from './admin/ReporteGuaraniPage';
import GestionarDocentesPage from './admin/GestionarDocentesPage';
import ResumenNotasPage from './admin/ResumenNotasPage';
import AsistenciaPage from './admin/AsistenciaPage';
import ColaboradoresPage from './admin/ColaboradoresPage';

const PanelAdmin: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [user, setUser] = useState<any>(null);
  const [sidebarCollapsed] = useState(false);
  const permissions = usePermissions();
  const [notifications] = useState([
    { id: 1, message: 'Nueva inscripción en Matemática I', time: '2 min ago', type: 'info' },
    { id: 2, message: 'Turno tarde completado', time: '1 hora ago', type: 'warning' },
  ]);

  // Usar el usuario del contexto de autenticación
  useEffect(() => {
    console.log('PanelAdmin useEffect - authUser:', authUser);
    
    if (authUser) {
      console.log('Setting user from authUser:', authUser);
      setUser(authUser);
    } else {
      // Si no hay usuario en el contexto, intentar cargar desde localStorage
      const userData = localStorage.getItem('user');
      const token = localStorage.getItem('token');
      
      console.log('No authUser, checking localStorage - userData:', userData, 'token:', token);
      
      if (!userData || !token) {
        navigate('/login');
        return;
      }

      try {
        const user = JSON.parse(userData);
        console.log('Setting user from localStorage:', user);
        setUser(user);
      } catch (error) {
        console.error('Error parsing user data:', error);
        navigate('/login');
      }
    }
  }, [authUser, navigate]);

  useEffect(() => {
    if (!user) return;

    // Redirección automática según el rol del usuario
    const currentPath = location.pathname;
    if (currentPath === '/admin' || currentPath === '/admin/') {
      switch (user.rol) {
        case 'ayudante':
          navigate('/admin/ayudante-dashboard');
          break;
        case 'profesor':
          navigate('/admin/profesor-dashboard');
          break;
        case 'jtp':
          navigate('/admin/jtp-dashboard');
          break;
        case 'admin':
          // Los administradores pueden acceder al dashboard general
          break;
        default:
          navigate('/admin/ayudante-dashboard');
      }
    }
  }, [user, navigate, location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const getNavigationByRole = () => {
    const userRole = user?.rol;
    
    switch (userRole) {
      case 'admin':
        return [
          { 
            name: 'Dashboard', 
            href: '/admin', 
            icon: HomeIcon, 
            badge: null,
            permission: 'canViewDashboard' as keyof typeof permissions
          },
          { 
            name: 'Alumnos', 
            href: '/admin/alumnos', 
            icon: UserGroupIcon, 
            badge: '2',
            permission: 'canViewAlumnos' as keyof typeof permissions
          },
          { 
            name: 'Profesores', 
            href: '/admin/profesores', 
            icon: AcademicCapIcon, 
            badge: '1',
            permission: 'canViewProfesores' as keyof typeof permissions
          },
          { 
            name: 'Cursos', 
            href: '/admin/cursos', 
            icon: BookOpenIcon, 
            badge: '2',
            permission: 'canViewCursos' as keyof typeof permissions
          },
          { 
            name: 'Gestionar Docentes', 
            href: '/admin/gestionar-docentes', 
            icon: UserGroupIcon, 
            badge: null,
            permission: 'canViewCursos' as keyof typeof permissions
          },
          { 
            name: 'Turnos', 
            href: '/admin/turnos', 
            icon: ClockIcon, 
            badge: '2',
            permission: 'canViewTurnos' as keyof typeof permissions
          },
          { 
            name: 'Inscripciones', 
            href: '/admin/inscripciones', 
            icon: ClipboardDocumentListIcon, 
            badge: '2',
            permission: 'canViewInscripciones' as keyof typeof permissions
          },
          { 
            name: 'Notas', 
            href: '/admin/notas', 
            icon: ChartBarIcon, 
            badge: null,
            permission: 'canCargarNotas' as keyof typeof permissions
          },
          { 
            name: 'Rectificar Notas', 
            href: '/admin/rectificar-notas', 
            icon: PencilSquareIcon, 
            badge: null,
            permission: 'canViewCursos' as keyof typeof permissions
          },
          { 
            name: 'Resumen de Notas', 
            href: '/admin/resumen-notas', 
            icon: ClipboardDocumentListIcon, 
            badge: null,
            permission: 'canViewCursos' as keyof typeof permissions
          },
          { 
            name: 'Asistencia', 
            href: '/admin/asistencia', 
            icon: ClipboardDocumentCheckIcon, 
            badge: null,
            permission: 'canViewCursos' as keyof typeof permissions
          },
          { 
            name: 'Posibles Colaboradores', 
            href: '/admin/colaboradores', 
            icon: UserGroupIcon, 
            badge: null,
            permission: 'canViewCursos' as keyof typeof permissions
          },
        ];

      case 'jtp':
        return [
          { 
            name: 'Dashboard JTP', 
            href: '/admin/jtp-dashboard', 
            icon: HomeIcon, 
            badge: null,
            permission: 'canViewCursos' as keyof typeof permissions
          },
          { 
            name: 'Alumnos', 
            href: '/admin/alumnos', 
            icon: UserGroupIcon, 
            badge: '2',
            permission: 'canViewAlumnos' as keyof typeof permissions
          },
          { 
            name: 'Profesores', 
            href: '/admin/profesores', 
            icon: AcademicCapIcon, 
            badge: '1',
            permission: 'canViewProfesores' as keyof typeof permissions
          },
          { 
            name: 'Turnos', 
            href: '/admin/turnos', 
            icon: ClockIcon, 
            badge: '2',
            permission: 'canViewTurnos' as keyof typeof permissions
          },
          { 
            name: 'Inscripciones', 
            href: '/admin/inscripciones', 
            icon: ClipboardDocumentListIcon, 
            badge: '2',
            permission: 'canViewInscripciones' as keyof typeof permissions
          },
          { 
            name: 'Notas', 
            href: '/admin/notas', 
            icon: ChartBarIcon, 
            badge: null,
            permission: 'canCargarNotas' as keyof typeof permissions
          },
          { 
            name: 'Rectificar Notas', 
            href: '/admin/rectificar-notas', 
            icon: PencilSquareIcon, 
            badge: null,
            permission: 'canViewCursos' as keyof typeof permissions
          },
          { 
            name: 'Listado de Notas', 
            href: '/admin/listado-notas', 
            icon: ChartBarIcon, 
            badge: null,
            permission: 'canViewCursos' as keyof typeof permissions
          },
          { 
            name: 'Reporte Guaraní', 
            href: '/admin/reporte-guarani', 
            icon: ChartBarIcon, 
            badge: null,
            permission: 'canViewCursos' as keyof typeof permissions
          },
          { 
            name: 'Resumen de Notas', 
            href: '/admin/resumen-notas', 
            icon: ClipboardDocumentListIcon, 
            badge: null,
            permission: 'canViewCursos' as keyof typeof permissions
          },
          { 
            name: 'Asistencia', 
            href: '/admin/asistencia', 
            icon: ClipboardDocumentCheckIcon, 
            badge: null,
            permission: 'canViewCursos' as keyof typeof permissions
          },
        ];

      case 'ayudante':
        return [
          { 
            name: 'Dashboard', 
            href: '/admin/ayudante-dashboard', 
            icon: HomeIcon, 
            badge: null,
            permission: 'canViewInscripciones' as keyof typeof permissions
          },
          { 
            name: 'Cargar Notas', 
            href: '/admin/notas', 
            icon: ChartBarIcon, 
            badge: null,
            permission: 'canCargarNotas' as keyof typeof permissions
          },
          { 
            name: 'Asistencia', 
            href: '/admin/asistencia', 
            icon: ClipboardDocumentCheckIcon, 
            badge: null,
            permission: 'canCargarNotas' as keyof typeof permissions
          },
        ];

      case 'profesor':
        return [
          { 
            name: 'Dashboard Profesor', 
            href: '/admin/profesor-dashboard', 
            icon: HomeIcon, 
            badge: null,
            permission: 'canViewInscripciones' as keyof typeof permissions
          },
          { 
            name: 'Inscripciones', 
            href: '/admin/inscripciones', 
            icon: ClipboardDocumentListIcon, 
            badge: '2',
            permission: 'canViewInscripciones' as keyof typeof permissions
          },
          { 
            name: 'Notas', 
            href: '/admin/notas', 
            icon: ChartBarIcon, 
            badge: null,
            permission: 'canCargarNotas' as keyof typeof permissions
          },
        ];

      default:
        return [
          { 
            name: 'Dashboard', 
            href: '/admin', 
            icon: HomeIcon, 
            badge: null,
            permission: 'canViewDashboard' as keyof typeof permissions
          },
        ];
    }
  };

  const navigation = getNavigationByRole();

  const getPageTitle = () => {
    const currentPage = navigation.find(item => item.href === location.pathname);
    return currentPage ? currentPage.name : 'Dashboard';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Top Navigation Bar */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* Left side - Logo and Title */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                  <AcademicCapIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">Portal Académico</h1>
                  <p className="text-xs text-gray-500">Panel de Administración</p>
                </div>
              </div>
            </div>

            {/* Center - Page Title */}
            <div className="hidden md:block">
              <h2 className="text-lg font-semibold text-gray-800">{getPageTitle()}</h2>
            </div>

            {/* Right side - User Menu and Actions */}
            <div className="flex items-center space-x-4">
              {/* Notifications */}
              <div className="relative">
                <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                  <BellIcon className="h-5 w-5" />
                  {notifications.length > 0 && (
                    <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {notifications.length}
                    </span>
                  )}
                </button>
              </div>

              {/* User Menu */}
              <div className="relative">
                <button className="flex items-center space-x-2 p-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                  <UserCircleIcon className="h-8 w-8 text-gray-400" />
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-medium text-gray-900">{user?.nombre} {user?.apellido}</p>
                    <p className="text-xs text-gray-500 capitalize">{user?.rol}</p>
                  </div>
                  <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                </button>
              </div>

              {/* Logout Button */}
              <button
                onClick={handleLogout}
                className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors flex items-center space-x-2"
              >
                <ArrowRightOnRectangleIcon className="h-4 w-4" />
                <span className="hidden sm:inline">Cerrar Sesión</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <div className={`${sidebarCollapsed ? 'w-20' : 'w-72'} bg-gradient-to-b from-white to-gray-50 shadow-xl border-r border-gray-200 transition-all duration-300 min-h-screen`}>
          <nav className="mt-6 px-4">
            <div className="space-y-1">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <ProtectedNavLink
                    key={item.name}
                    to={item.href}
                    permission={item.permission}
                    className={`group flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 relative ${
                      isActive
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg transform scale-105'
                        : 'text-gray-700 hover:bg-blue-50 hover:text-blue-700 hover:shadow-sm'
                    }`}
                  >
                    {isActive && (
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-white rounded-r-full"></div>
                    )}
                    <item.icon
                      className={`${sidebarCollapsed ? 'mx-auto' : 'mr-3'} h-5 w-5 flex-shrink-0 ${
                        isActive ? 'text-white' : 'text-gray-500 group-hover:text-blue-600'
                      }`}
                    />
                    {!sidebarCollapsed && (
                      <div className="flex items-center justify-between w-full min-w-0">
                        <span className="truncate">{item.name}</span>
                        {item.badge && (
                          <span className={`inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold rounded-full ml-2 flex-shrink-0 ${
                            isActive 
                              ? 'bg-white text-blue-600' 
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {item.badge}
                          </span>
                        )}
                      </div>
                    )}
                  </ProtectedNavLink>
                );
              })}
            </div>

            {/* Quick Actions - Solo para admin */}
            {!sidebarCollapsed && user?.rol === 'admin' && (
              <div className="mt-8 pt-6 border-t border-gray-200">
                <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  Acciones Rápidas
                </h3>
                <div className="space-y-2">
                  <Link 
                    to="/admin/cursos" 
                    className="w-full flex items-center px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900 rounded-lg transition-colors"
                  >
                    <PlusIcon className="mr-3 h-4 w-4" />
                    Nuevo Curso
                  </Link>
                  <Link 
                    to="/admin/profesores" 
                    className="w-full flex items-center px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900 rounded-lg transition-colors"
                  >
                    <PlusIcon className="mr-3 h-4 w-4" />
                    Nuevo Profesor
                  </Link>
                </div>
              </div>
            )}
          </nav>
        </div>


        {/* Main Content */}
        <div className="flex-1 p-6">
          <div className="max-w-7xl mx-auto">
            <Routes>
              <Route path="/alumnos" element={
                permissions.canViewAlumnos ? <AlumnosPage /> : <AccessDenied />
              } />
              <Route path="/profesores" element={
                permissions.canViewProfesores ? <ProfesoresPage /> : <AccessDenied />
              } />
              <Route path="/cursos" element={
                permissions.canViewCursos ? <CursosPage /> : <AccessDenied />
              } />
              <Route path="/gestionar-docentes" element={
                user?.rol === 'admin' ? <GestionarDocentesPage /> : <AccessDenied />
              } />
              <Route path="/turnos" element={
                permissions.canViewTurnos ? <TurnosPage /> : <AccessDenied />
              } />
              <Route path="/inscripciones" element={
                permissions.canViewInscripciones ? <InscripcionesPage /> : <AccessDenied />
              } />
              <Route path="/notas" element={
                permissions.canCargarNotas ? <NotasPage /> : <AccessDenied />
              } />
              <Route path="/rectificar-notas" element={
                (permissions.canViewCursos || user?.rol === 'jtp' || user?.rol === 'admin') ? <RectificarNotasPage /> : <AccessDenied />
              } />
              <Route path="/listado-notas" element={
                (permissions.canViewCursos || user?.rol === 'jtp' || user?.rol === 'admin') ? <ListadoNotasPage /> : <AccessDenied />
              } />
              <Route path="/reporte-guarani" element={
                (permissions.canViewCursos || user?.rol === 'jtp' || user?.rol === 'admin') ? <ReporteGuaraniPage /> : <AccessDenied />
              } />
              <Route path="/resumen-notas" element={
                (permissions.canViewCursos || user?.rol === 'jtp' || user?.rol === 'admin') ? <ResumenNotasPage /> : <AccessDenied />
              } />
              <Route path="/asistencia" element={
                (permissions.canCargarNotas || user?.rol === 'jtp' || user?.rol === 'admin') ? <AsistenciaPage /> : <AccessDenied />
              } />
              <Route path="/colaboradores" element={
                user?.rol === 'admin' ? <ColaboradoresPage /> : <AccessDenied />
              } />
              <Route path="/ayudante-dashboard" element={
                permissions.canViewInscripciones ? <AyudanteDashboard /> : <AccessDenied />
              } />
              <Route path="/profesor-dashboard" element={
                permissions.canViewInscripciones ? <ProfesorDashboard /> : <AccessDenied />
              } />
              <Route path="/jtp-dashboard" element={
                permissions.canViewCursos ? <JTPDashboard /> : <AccessDenied />
              } />
              <Route path="/" element={
                permissions.canViewDashboard ? <Dashboard /> : <AccessDenied />
              } />
            </Routes>
          </div>
        </div>
      </div>

      {/* Notifications Panel (Hidden by default) */}
      <div className="hidden fixed right-4 top-20 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Notificaciones</h3>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {notifications.map((notification) => (
            <div key={notification.id} className="p-4 border-b border-gray-100 hover:bg-gray-50">
              <p className="text-sm text-gray-900">{notification.message}</p>
              <p className="text-xs text-gray-500 mt-1">{notification.time}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Dashboard: React.FC = () => {
  // Fetch estadísticas del dashboard
  const { data: stats, isLoading: loadingStats } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: async () => {
      try {
        const response = await axios.get('/dashboard/stats');
        return response.data;
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
        return {
          alumnos: 0,
          profesores: 0,
          cursos: 0,
          turnos: 0,
          inscripciones: 0,
          notas: 0,
        };
      }
    },
    refetchOnWindowFocus: false,
    staleTime: 30000, // Cache por 30 segundos
  });

  const quickActions = [
    { name: 'Crear Curso', href: '/admin/cursos', icon: BookOpenIcon, color: 'blue' },
    { name: 'Agregar Profesor', href: '/admin/profesores', icon: AcademicCapIcon, color: 'green' },
    { name: 'Nuevo Turno', href: '/admin/turnos', icon: ClockIcon, color: 'purple' },
    { name: 'Ver Inscripciones', href: '/admin/inscripciones', icon: ClipboardDocumentListIcon, color: 'orange' },
  ];

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">¡Bienvenido al Panel de Administración!</h1>
            <p className="text-blue-100 mt-1">Gestiona tu portal académico de manera eficiente</p>
          </div>
          <div className="hidden md:block">
            <AcademicCapIcon className="h-16 w-16 text-blue-200" />
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <UserGroupIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Alumnos</p>
              <p className="text-2xl font-bold text-gray-900">
                {loadingStats ? '...' : (stats?.alumnos || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <AcademicCapIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Profesores</p>
              <p className="text-2xl font-bold text-gray-900">
                {loadingStats ? '...' : (stats?.profesores || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <BookOpenIcon className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Cursos</p>
              <p className="text-2xl font-bold text-gray-900">
                {loadingStats ? '...' : (stats?.cursos || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 rounded-lg">
              <ClockIcon className="h-6 w-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Turnos</p>
              <p className="text-2xl font-bold text-gray-900">
                {loadingStats ? '...' : (stats?.turnos || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <ClipboardDocumentListIcon className="h-6 w-6 text-indigo-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Inscripciones</p>
              <p className="text-2xl font-bold text-gray-900">
                {loadingStats ? '...' : (stats?.inscripciones || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <ChartBarIcon className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Notas</p>
              <p className="text-2xl font-bold text-gray-900">
                {loadingStats ? '...' : (stats?.notas || 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Acciones Rápidas</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickActions.map((action) => (
            <Link
              key={action.name}
              to={action.href}
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:shadow-md hover:border-gray-300 transition-all duration-200"
            >
              <div className={`p-2 bg-${action.color}-100 rounded-lg`}>
                <action.icon className={`h-5 w-5 text-${action.color}-600`} />
              </div>
              <span className="ml-3 text-sm font-medium text-gray-900">{action.name}</span>
            </Link>
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Actividad Reciente</h3>
        <div className="space-y-4">
          <div className="flex items-center space-x-3">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <p className="text-sm text-gray-600">Nueva inscripción en Matemática I</p>
            <span className="text-xs text-gray-400">hace 2 minutos</span>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            <p className="text-sm text-gray-600">Turno tarde completado</p>
            <span className="text-xs text-gray-400">hace 1 hora</span>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
            <p className="text-sm text-gray-600">Profesor Ariel Sobrado agregado</p>
            <span className="text-xs text-gray-400">hace 2 horas</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PanelAdmin;
